import { MayerFabricsService } from './services/mayer-fabrics.service';
import { ShipmentService } from 'src/app/acumatica/EntityServices/Shipment.service';
import { BuildDetailsHttpService } from './services/build-details-http.service';
import { InvAdjustmentEntryDialog } from './common/inv-adjustment-entry/inv-adjustment-entry.component';
import { WarehouseSelectDialog } from './acumatica/warehouse-select-dialog/warehouse-select-dialog.component';
import { NativeElementInjectorDirective } from './directives/native-element-injector.directive';
import { SOTypeSelectorComponent } from './common/so-type-selector/so-type-selector.component';

// Angular
import { APP_INITIALIZER, ErrorHandler, InjectionToken, isDevMode, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

// Angular Materials modules
import { MatDialogModule } from '@angular/material/dialog';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatSliderModule } from '@angular/material/slider';
import { MatTreeModule } from '@angular/material/tree';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';

// Acumatica integration
import { AcumaticaService } from './acumatica/acumatica.service';
import { AcumaticaModule } from './acumatica/acumatica.module';
import { AcumaticaLoginComponent } from './acumatica/acumatica-login/acumatica-login.component';

// Common app functionality extensions
import { ConfirmationDialog } from './common/confirmation-dialog/confirmation-dialog.component';
import { AlertDialog } from './common/alert-dialog/alert-dialog.component';
import { PasswordDialog } from './common/password-dialog/password-dialog.component';
import { FeedbackSnackbarComponent } from './common/feedback-snackbar/feedback-snackbar.component';
import { TitleCasePipe } from './common/title-case.pipe';
import { AutofocusDirective } from './directives/autofocus.directive';
import { UserDateTimePipe } from './common/user-datetime.pipe';
import { ConditionFilterPipe } from './common/condition-filter.pipe';

// Navigation & Errors
import { ErrorHandlerService } from './services/error-handler.service';
import { AuthGuard_AdminUser, AuthGuard_DEV, AuthGuard_LoggedIn, AuthGuard_Rep, AuthGuard_WMS } from './services/auth-guard';
import { PendingChangesGuard } from './services/dirty-nav-guard';
import { NavMenuComponent } from './application-navigator/application-navigator.component';

// WMS Base Modules
import { AppComponent, WMSFormDirective } from './app.component';
import { MatComponentsModule } from './mat-components.module';

// WMS facilities
import { QuantityPipe, QuantityFractionPipe } from './common/quantity.pipe';
import { WrapRightPipe } from './common/wrapRight.pipe';
import { SignedNumberPipe as SignedNumberPipe } from './common/signed-number.pipe';

import { ScanInputDirective } from './directives/scan-input.directive';
import { QuantityInputDirective } from './directives/quantity-input.directive';

import { WarehouseSelectorComponent } from './common/warehouse-selector/warehouse-selector.component';
import { ItemThumbnailComponent } from './common/item-thumbnail/item-thumbnail.component';
import { CameraCaptureComponent } from './common/camera-capture/camera-capture.component';
import { InventoryAdjustmentEditorComponent } from './common/inventory-adjustment-editor/inventory-adjustment-editor.component';
import { InventoryLocationEditorComponent } from './common/inventory-location-editor/inventory-location-editor.component';
import { LabelPrintDialogComponent } from './screens/order-picking2/label-print-dialog/label-print-dialog.component';

import { EditInventoryDialog } from './screens/inventory-check/edit-inventory-dialog/edit-inventory-dialog.component';
import { PrinterSelectDialog } from './common/printer-selector/printer-selector.component';

// core application
import { AppConfig } from './app-config.service';
import { HomeDashboardComponent } from './screens/home-dashboard/home-dashboard.component';
import { HomeLoginComponent } from './screens/home-login/home-login.component';
import { SandboxComponent } from './sandbox/sandbox.component';

// WMS Screens
import { OrderPickingGetShipmentComponent } from './screens/order-picking-get-shipment/order-picking-get-shipment.component';
// import { OrderPickingComponent } from './screens/order-picking/order-picking.component';
import { OrderPicking2Component } from './screens/order-picking2/order-picking2.component';
import { PickDetailEditorComponent } from './screens/order-picking2/pick-detail-editor/pick-detail-editor.component';
import { PickCompletionDialogComponent } from './screens/order-picking2/pick-completion-dialog/pick-completion-dialog.component';
import { LocationInquiryComponent } from './screens/location-inquiry/location-inquiry.component';
import { AppSettingsComponent } from './common/app-settings/app-settings.component';
import { LocationTransferComponent } from './screens/location-transfer/location-transfer.component';
import { NoteEditorDialog } from './common/note-editor-dialog/note-editor-dialog.component';
import { ReceivingGetReceiptComponent } from './screens/receiving-get-receipt/receiving-get-receipt.component';
import { ReceivingComponent } from './screens/receiving/receiving.component';
import { DeveloperComponent } from './screens/developer/developer.component';
import { LogDataReprocessorComponent } from './screens/log-data-reprocessor/log-data-reprocessor.component';
import { InventoryCheckComponent } from './screens/inventory-check/inventory-check.component';
import { InventoryCheckService } from './acumatica/EntityServices/StockCheck.service';
import { WarehouseSelectionDialogComponent } from './common/warehouse-selection-dialog/warehouse-selection-dialog.component';
import { RestockHeldInventoryComponent } from './screens/inventory-check/restock-held-inventory/restock-held-inventory.component';
import { CutHoldPullComponent } from './screens/cut-hold-pull/cut-hold-pull.component';
import { CutHoldPullGetShipmentComponent } from './screens/cut-hold-pull-get-shipment/cut-hold-pull-get-shipment.component';
import { LocationAuditComponent } from './screens/location-audit/location-audit.component';

// Sales Rep Screens
import { BinderSelectionComponent } from './screens/binder-audit/binder-selection/binder-selection.component';
import { CardAuditComponent } from './screens/binder-audit/card-audit/card-audit.component';
import { CardAuditReviewComponent } from './screens/binder-audit/card-audit-review/card-audit-review.component';
import { KitAuditTypeSelectionComponent } from './screens/kit-audit/audit-type/audit-type.component';
import { KitAuditSelectKitComponent } from './screens/kit-audit/kit-selection/kit-selection.component';
import { KitAuditAuditKit } from './screens/kit-audit/audit-kit/audit-kit.component';
import { RepOrderItemComponent } from './screens/rep-order-entry/rep-order-item/order-item.component';

// Sales Rep & WMS shared screens
import { ItemInquiryComponent } from './screens/item-inquiry/item-inquiry.component';
import { FillRepCardsComponent } from './screens/binder-audit/fill-rep-cards/fill-rep-cards.component';
import { SalesRepReceiptComponent } from './screens/sales-rep-receipt/sales-rep-receipt.component';
import { CustomerIDSelectorComponent } from './common/customer-id-selector/customer-id-selector.component';
import { CustomerInfoComponent } from './screens/customer-info/customer-info.component';
import { RepInventoryComponent } from './screens/rep-inventory/rep-inventory.component';
import { RepOrderEntryComponent } from './screens/rep-order-entry/rep-order-entry.component';
import { InventoryIDSelectorComponent } from './common/inventory-id-selector/inventory-id-selector.component';
import { RepInventoryInitializationComponent } from './screens/rep-inventory-initialization/rep-inventory-initialization.component';
import { QuantityInputComponent } from './common/quantity-input/quantity-input.component';
import { WarehouseLocationSelectorComponent } from './common/warehouse-location-selector/warehouse-location-selector.component';
import { SoShiptoInputComponent } from './common/so-shipto-input/so-shipto-input.component';
import { CountrySelectorComponent } from './common/country-selector/country-selector.component';
import { NoticeDisplayComponent } from './screens/kit-audit/notice-display/notice-display.component';
import { Nl2brPipe } from './common/nl2br.pipe';
import { MatNativeDateModule } from '@angular/material/core';

const routes: Routes = [
  // global
  { path: 'login', component: HomeLoginComponent },
  { path: '', component: HomeDashboardComponent, canActivate: [AuthGuard_LoggedIn] },

  // user-secured common -- AuthGuard_LoggedIn
  { path: 'item/:inventoryID', component: ItemInquiryComponent, canActivate: [AuthGuard_LoggedIn] },
  { path: 'item', component: ItemInquiryComponent, canActivate: [AuthGuard_LoggedIn] },
  { path: 'inventory-check', component: InventoryCheckComponent, canActivate: [AuthGuard_LoggedIn], canDeactivate: [PendingChangesGuard] },
  { path: 'customer-info/:customerID', component: CustomerInfoComponent, canActivate: [AuthGuard_LoggedIn] },
  { path: 'customer-info', component: CustomerInfoComponent, canActivate: [AuthGuard_LoggedIn] },
  { path: 'my-inventory', component: RepInventoryComponent, canActivate: [AuthGuard_LoggedIn] },
  { path: 'my-inventory-initialize', component: RepInventoryInitializationComponent, canActivate: [AuthGuard_LoggedIn], canDeactivate: [PendingChangesGuard] },

  // WMS only -- AuthGuard_WMS
  { path: 'location/:warehouseID/:locationID', component: LocationInquiryComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS] },
  { path: 'location/:warehouseID', component: LocationInquiryComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS] },
  { path: 'location', component: LocationInquiryComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS] },
  { path: 'location-audit', component: LocationAuditComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS] },
  { path: 'picking/:shipmentNbr', component: OrderPicking2Component, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS], canDeactivate: [PendingChangesGuard] },
  { path: 'picking', component: OrderPickingGetShipmentComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS] },
  { path: 'receiving/:receiptNbr', component: ReceivingComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS], canDeactivate: [PendingChangesGuard] },
  { path: 'receiving', component: ReceivingGetReceiptComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS] },
  { path: 'transfer', component: LocationTransferComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS], canDeactivate: [PendingChangesGuard] },
  { path: 'holdpull/:shipmentNbr', component: CutHoldPullComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS], canDeactivate: [PendingChangesGuard] },
  { path: 'holdpull', component: CutHoldPullGetShipmentComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_WMS] },

  // Sales Rep only -- AuthGuard_Rep
  { path: 'kit-audit', component: KitAuditTypeSelectionComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },
  { path: 'kit-audit/select', component: KitAuditSelectKitComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },
  { path: 'kit-audit/audit', component: KitAuditAuditKit, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },

  { path: 'binder-audit', component: BinderSelectionComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },
  { path: 'binder-audit/review', component: CardAuditReviewComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },
  { path: 'binder-audit/:inventoryID', component: CardAuditComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },
  { path: 'binder-audit/fill-rep-cards/:orderNbr', component: FillRepCardsComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },
  { path: 'sales-rep-receipt', component: SalesRepReceiptComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep] },
  { path: 'order-entry/:orderType', component: RepOrderEntryComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_Rep], canDeactivate: [PendingChangesGuard] },

  // admin / DEV
  { path: 'log-data-reprocessor', component: LogDataReprocessorComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_AdminUser] },
  { path: 'settings', component: AppSettingsComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_AdminUser] },
  { path: 'dev', component: DeveloperComponent, canActivate: [AuthGuard_LoggedIn, AuthGuard_DEV] },
  // { path: 'sb', component: SandboxComponent },
];

export const APP_MENU_ITEMS = new InjectionToken('APPLICATION-MAIN-MENU-ITEMS');
const applicationMenuItems: MenuItem[] = [
  {
    path: 'picking',
    icon: 'shopping_cart_outline',
    label: 'Order Picking',
    // colspan: 2,
    condition: (env: any) => env.mode == 'wms'
  },
  {
    path: 'receiving',
    icon: 'local_shipping',
    label: 'Receiving',
    condition: (env: any) => env.mode == 'wms'
  },
  {
    path: 'transfer',
    icon: 'moving',
    label: 'Transfer /\nPut-away',
    condition: (env: any) => env.mode == 'wms'
  },
  {
    path: 'inventory-check',
    icon: 'fact_check',
    label: 'Inventory Check',
    condition: (env: any) => env.mode == 'wms',
    loaded: (item: MenuItem, acumatica: AcumaticaService) => {
      setTimeout(() => {
        let stockChecks = acumatica.injector.get(InventoryCheckService);
        stockChecks.getStockChecks({ loader: null }).subscribe(stockChecks => {
          let count = stockChecks.Details.map(detail => detail.InventoryID).unique().length;
          if (count == 0) return;
          item.label = "Inventory Check\n(" + count + ")"
        });
      }, 500);
    }
  },
  {
    path: 'my-inventory-initialize',
    icon: 'shop_two',
    label: 'Adjust in Inventory',
    condition: (env: any) => env.mode == 'wms'
  },
  {
    path: 'item',
    icon: 'not_listed_location',
    label: 'Item Inquiry',
    condition: (env: any) => env.mode == 'wms'
  },
  {
    path: 'location',
    icon: 'shelves',
    label: 'Location Inquiry',
    condition: (env: any) => env.mode == 'wms'
  },
  {
    path: 'location-audit',
    icon: 'warehouse',
    label: 'Location Audit',
    condition: (env: any) => env.mode == 'wms'
  },

  {
    path: 'customer-info',
    icon: 'apartment',
    label: 'Customer Info',
    condition: (env: any) => env.mode == 'rep'
  },
  {
    path: 'order-entry/SO',
    icon: 'shopping_cart',
    label: 'Customer Order',
    condition: (env: any) => env.mode == 'rep'
  },
  // {
  //   path: 'binder-audit',
  //   icon: 'auto_stories',
  //   label: 'Binder Audit\n(Obsolete)',
  //   condition: (env: any) => env.mode == 'rep'
  // },
  {
    path: 'kit-audit',
    icon: 'checklist',
    label: 'Kit Audit',
    condition: (env: any) => env.mode == 'rep'
  },
  {
    path: 'item',
    icon: 'not_listed_location',
    label: 'Item Inquiry',
    condition: (env: any) => env.mode == 'rep'
  },

  {
    break: true,
    path: 'order-entry/TR',
    icon: 'inventory_2',
    label: 'Order Supplies',
    condition: (env: any) => env.mode == 'rep'
  },
  {
    path: 'sales-rep-receipt',
    icon: 'local_shipping',
    label: 'Receive Shipment',
    condition: (env: any) => env.mode == 'rep',
    loaded: (item: MenuItem, acumatica: AcumaticaService) => {
      let mayerfabrics = acumatica.injector.get(MayerFabricsService);
      setTimeout(() => {
        let whseID = mayerfabrics.RepWarehouseID;
        if (!whseID) return;

        let shipments = acumatica.injector.get(ShipmentService);
        shipments.getSalesRepUnreceivedShipments(whseID).subscribe(shipments => {
          if (shipments.length == 0) return;
          item.label = "Receive Shipment\n(" + shipments.length + " Unreceived)"
        });
      }, 500);
    }
  },
  {
    path: 'my-inventory',
    icon: 'warehouse',
    label: 'My Inventory',
    condition: (env: any) => env.mode == 'rep'
  },
  {
    path: 'my-inventory-initialize',
    icon: 'shop_two',
    label: 'Adjust in Inventory',
    condition: (env: any) => env.mode == 'rep'
  },
  {
    path: 'holdpull',
    icon: 'inventory',
    label: 'Cut & Hold Pull',
    condition: (env: any) => env.mode == 'wms'
  },

  {
    break: true,
    path: 'settings',
    icon: 'settings',
    label: 'Settings',
    condition: (env: any, acumatica: AcumaticaService) => isDevMode() || acumatica.username == 'NickH' || env.mode == 'wms'
  },
  {
    path: 'dev',
    icon: 'adb',
    label: 'Developer',
    condition: (env: any, acumatica: AcumaticaService) => isDevMode() || acumatica.username == 'NickH'
  },
  {
    path: 'log-data-reprocessor',
    icon: 'rebase_edit',
    label: 'Log Data Reprocess',
    condition: (env: any, acumatica: AcumaticaService) => env.mode == 'wms' && acumatica.username == 'NickH'
  },

]

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    MatComponentsModule,
    MatDialogModule,
    MatGridListModule,
    MatSliderModule,
    MatButtonToggleModule,
    MatTreeModule,
    MatDatepickerModule,
    MatNativeDateModule,

    AcumaticaModule,
    RouterModule.forRoot(routes),
  ],
  declarations: [
    AppComponent,

    // Pipes
    QuantityPipe,
    QuantityFractionPipe,
    UserDateTimePipe,
    TitleCasePipe,
    WrapRightPipe,
    SignedNumberPipe,
    Nl2brPipe,
    ConditionFilterPipe,

    // Directives
    ScanInputDirective,
    // ScanInputV2Directive,
    QuantityInputDirective,
    AutofocusDirective,
    NativeElementInjectorDirective,

    // Common dialogs & templates
    PrinterSelectDialog,
    ConfirmationDialog,
    AlertDialog,
    PasswordDialog,
    WarehouseSelectDialog,
    FeedbackSnackbarComponent,

    // Core application
    NavMenuComponent,

    // WMS shared / common components
    WMSFormDirective,
    AcumaticaLoginComponent,
    WarehouseSelectorComponent,
    SOTypeSelectorComponent,
    WarehouseLocationSelectorComponent,
    ItemThumbnailComponent,
    CameraCaptureComponent,

    // main application components
    HomeLoginComponent,
    HomeDashboardComponent,
    ItemInquiryComponent,
    LocationInquiryComponent,
    AppSettingsComponent,
    LocationTransferComponent,
    OrderPickingGetShipmentComponent,
    SandboxComponent,

    // OrderPickingComponent,
    OrderPicking2Component,
    PickDetailEditorComponent,
    PickCompletionDialogComponent,

    NoteEditorDialog,
    InvAdjustmentEntryDialog,
    ReceivingGetReceiptComponent,
    ReceivingComponent,
    DeveloperComponent,
    BinderSelectionComponent,
    CardAuditComponent,
    CardAuditReviewComponent,
    FillRepCardsComponent,
    SalesRepReceiptComponent,
    FeedbackSnackbarComponent,
    CustomerIDSelectorComponent,
    CustomerInfoComponent,
    RepInventoryComponent,
    RepOrderEntryComponent,
    InventoryIDSelectorComponent,
    LogDataReprocessorComponent,
    RepInventoryInitializationComponent,
    QuantityInputComponent,
    RepOrderItemComponent,
    InventoryAdjustmentEditorComponent,
    InventoryLocationEditorComponent,
    LabelPrintDialogComponent,
    InventoryCheckComponent,
    WarehouseSelectionDialogComponent,
    EditInventoryDialog,
    RestockHeldInventoryComponent,
    WarehouseLocationSelectorComponent,
    SoShiptoInputComponent,
    CountrySelectorComponent,
    LocationAuditComponent,
    CutHoldPullGetShipmentComponent,
    CutHoldPullComponent,
    
    KitAuditTypeSelectionComponent,
    KitAuditSelectKitComponent,
    KitAuditAuditKit,
    NoticeDisplayComponent,
  ],
  entryComponents: [
    AlertDialog,
  ],
  providers: [
    // app-wide error handler
    { provide: ErrorHandler, useClass: ErrorHandlerService },

    // dirty navigation warning
    PendingChangesGuard,

    // start service to periodically poll server for updated build
    {
      provide: APP_INITIALIZER, multi: true,
      deps: [BuildDetailsHttpService],
      useFactory: (buildDetailsService: BuildDetailsHttpService) => {
        return () => buildDetailsService.fetchBuildDetails();
      },
    },

    // application initializer -- load environment-specific config file
    AppConfig,
    {
      provide: APP_INITIALIZER, multi: true,
      deps: [AppConfig],
      useFactory: (appConfig: AppConfig) => {
        return () => appConfig.loadAppConfig()
      },
    },

    { provide: APP_MENU_ITEMS, useValue: applicationMenuItems }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

export interface MenuItem {
  path: string,
  icon: string,
  label: string,
  break?: boolean,
  colspan?: number,
  condition?: (env: any, acumatica: AcumaticaService) => boolean,
  loaded?: (item: MenuItem, acumatica: AcumaticaService) => void,
  disabled?: boolean
}

declare global {
  interface Array<T> {
    group<T>(
      grouping: {
        by: string | string[],
        sum?: string | string[]
      },
      underKey: string,
      details: string | string[]
    ): Array<T>;

    unique<T>(): Array<T>;

    /** Invokes the callback function for each unique value in the array contents */
    forEachUnique<T>(callbackfn: (value: T, index: number, array: T[]) => void): void;

    /** Returns the sum of all elements in the numeric array. Optionally may provide a predicate to be used to map elements to their numeric component to be summed */
    sum(predicate?: (element: any) => number): number;

    /** Applies the predicate function on each element in the array and returns the array item whose predicate yielded the smallest value */
    min<T>(predicate: (value: T) => any): T | undefined;

    /** Removes the first occurrence of the provided value from the array contents */
    remove(value: any): void;
    /** Removes all equal-ish occurrences of the provided value from the array contents */
    removeAll(value: any): void;

    /** Returns a true or false if any of the elements satisfy the condition specified in the predicate */
    any<T>(predicate: (value: T, currentIndex: number, array: T[]) => boolean): boolean;

    /** Returns whether the array contains the given value -- array.indexOf(value) > -1 */
    contains(value: any): boolean;

    /**
     *  Search for the first element in the array that satisfies the condition starting with the specified index. Returns the index of that element or undefined if none are found
     * @param startIndex the 'current' index in the array to begin searching after
     * @param validator a function that receives the array elements and their index and should return true if the element satisfies the desired condition
     * @param checkFromStart a boolean value indicating if they array should be searched from the start upon reaching the end
     */
    findFrom(startIndex: number, validator: (element: any, index: number) => any, checkFromStart?: boolean): number | undefined;
  }

  interface Date {
    /** Returns a Date object encoded to standard ISO-8601 */
    toISO8601(): string;

    /**
     * Encodes the Date object as a 8 digit integer with the value of YYYYMMDD
     */
    toYYYYMMDD(): number;

    /**
     * Formats a Date object into a string using .NET-style custom format placeholders.
     * @param date The Date object to format.
     * @param formatString The format string (e.g., "yyyy-MM-dd hh:mm:ss tt" or "dddd, MMM dS, yyyy").
     * @returns The formatted date string.
     */
    format(formatString: string): string;

    /** Returns a Date object with the specified number of days added to it */
    addDays(days: number): Date;
  }

  interface String {
    toHtmlEntities(): string;
    reverse(): string;
  }

  interface Math {
    roundTo(value: number | undefined, precision: number): number;
  }
}

Array.prototype.findFrom = function (startIndex: number, validator: (element: any, index: number) => any, checkFromStart?: boolean): number | undefined {
  if (checkFromStart === undefined) checkFromStart = true;
  let checkIndex = startIndex;
  while (!validator(this[checkIndex], checkIndex)) {
    if (++checkIndex == this.length) {
      if (!checkFromStart) return undefined;
      checkIndex = 0;
    }
    if (checkIndex == startIndex) return undefined;
  }
  return checkIndex;
}

/*
 Given an array of objects with common (parent) fields and detail (child) fields
 Create a new Array grouping on parent fields with child details in a nested property

 original_array = [
   {ID:'A', Desc:'Alpha', LotSerialNbr:'abc', Quantity:2 },
   {ID:'A', Desc:'Alpha', LotSerialNbr:'def', Quantity:3 },
   {ID:'B', Desc:'Bravo', LotSerialNbr:'ghi', Quantity:4 },
 ]

 new_array = original_array.group({
   // group on fields
      by: ['ID', 'Desc'],
   // aggregate
      sum: 'Quantity',
 },
   // group children under name
      'Details',
   // child detail fields
      ['LotSerialNbr', 'Quantity']
 ) => [
   {ID:'A', Desc:'Alpha', Quantity:5, Details:[
     {LotSerialNbr:'abc', Quantity:2},
     {LotSerialNbr:'def', Quantity:3}
   ]},
   {ID:'B', Desc:'Bravo', Quantity:4, Details:[
     {LotSerialNbr:'ghi', Quantity:4}
   ]},
 ]

acumatica.get('WarehouseLocationInquiry', {filter_terms, expand='Results'});
interface LocationItemSet {
  InventoryID: string,
  pieces: {
    LotSerialNbr: string,
    Quantity: number
  }[]
}
groupedPieces = inquiry.Results.group<LocationItemSet>({ by: 'InventoryID' }, 'pieces', ['LotSerialNbr', 'Quantity']);
*/
Array.prototype.group = function <T>(group: {
  by: string | string[],
  sum?: string | string[]
}, groupField: string, _details: string | string[]): T[] {
  // cast strings into string[]s
  let by: string[] = typeof (group.by) === 'string' ? [group.by] : group.by;
  let sum: string[] | null = (group.sum === undefined ? null : (
    typeof (group.sum) === 'string' ? [group.sum] : group.sum
  ));
  let details: string[] = typeof (_details) === 'string' ? [_details] : _details;
  let dict: any = {};

  // iterate the array elements
  this.forEach(item => {
    // create a key from the group-by fields
    let key: string = by.map(field => item[field].toString()).join('`');
    // create a dictionary entry if necessary
    if (!dict[key]) {
      // initialize dictionary value
      dict[key] = {};
      // initialize group-by fields
      by.forEach(field => dict[key][field] = item[field]);
      // initialize aggregate sum
      sum?.forEach(field => dict[key][field] = 0);
      // initialize detail collection
      dict[key][groupField] = [];
    }
    // get the dictionary value
    let out = dict[key];
    // aggregate sum
    sum?.forEach(field => out[field] += item[field]);
    // create a detail collection item
    let detail: any = {};
    // add fields from main item into detail collection item
    details.forEach(field => detail[field] = item[field]);
    // put the detail collection into the output group
    out[groupField].push(detail);
  });

  return Object.values(dict);
}

Array.prototype.unique = function <T>(): Array<T> {
  return this.filter((v, i, a) => a.indexOf(v) === i);
}

Array.prototype.forEachUnique = function <T>(callbackfn: (value: T, index: number, array: T[]) => void): void {
  this
    .unique<T>()
    .forEach((v, i, a) => callbackfn(v, i, a));
}

Array.prototype.min = function <T>(predicate: (value: T) => any): T | undefined {
  let minValue: any;
  let minItem: T | undefined = undefined;
  this.forEach(item => {
    const itemValue = predicate(item);
    if (minValue === undefined || itemValue < minValue) {
      minValue = itemValue;
      minItem = item;
    }
  });

  return minItem;
}

Array.prototype.sum = function (mapFn?: (element: any) => number): number {
  mapFn ||= (value) => value;
  return this.reduce((total, value) => total + mapFn!(value), 0);
}

Array.prototype.remove = function (value: any): any {
  const idx = this.indexOf(value);
  return (idx == -1) ? undefined : this.splice(idx, 1);
}
Array.prototype.removeAll = function (value: any) {
  let indexes: number[] = [];
  this.forEach((item, idx) => { if (item == value) indexes.unshift(idx) });
  indexes.forEach(idx => this.splice(idx, 1));
}

Array.prototype.any = function <T>(predicate: (value: T, currentIndex: number, array: T[]) => boolean): boolean {
  return this.reduce((prev, curr, currentIndex, array) => prev || predicate(curr, currentIndex, array), false);
}

Array.prototype.contains = function (value: any) { return this.indexOf(value) > -1; }

Date.prototype.toISO8601 = function (): string {
  var tzo = -this.getTimezoneOffset(),
    dif = tzo >= 0 ? '+' : '-',
    pad = function (num: number) {
      var norm = Math.floor(Math.abs(num));
      return (norm < 10 ? '0' : '') + norm;
    };

  return this.getFullYear() +
    '-' + pad(this.getMonth() + 1) +
    '-' + pad(this.getDate()) +
    'T' + pad(this.getHours()) +
    ':' + pad(this.getMinutes()) +
    ':' + pad(this.getSeconds()) +
    dif + pad(tzo / 60) +
    ':' + pad(tzo % 60);
}

Date.prototype.toYYYYMMDD = function (separator?: string): number {
  return parseInt(this.getFullYear().toString()
    + (this.getMonth() + 1).toString().padStart(2, '0')
    + this.getDate().toString().padStart(2, '0'));
}
Date.prototype.format = function(formatString: string): string {
    const date = this;
    if (!date || isNaN(date.getTime())) {
        return '';
    }

    // --- Utility Arrays for Day/Month Names (Basic English) ---
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const getOrdinalSuffix = (day: number): string => {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    // --- Date Component Calculations ---

    const Y = date.getFullYear();
    const M = date.getMonth() + 1; // 1-12
    const D = date.getDate();
    const H = date.getHours();    // 0-23
    const h = H % 12 || 12;       // 1-12 (12-hour clock)
    const m = date.getMinutes();
    const s = date.getSeconds();
    const f = date.getMilliseconds();
    const t = H < 12 ? 'AM' : 'PM';
    const dayIndex = date.getDay(); // 0-6
    const monthIndex = date.getMonth(); // 0-11

    // --- Padded Values (Used for MM, dd, HH, hh, mm, ss) ---
    const pad = (num: number): string => String(num).padStart(2, '0');

    const values = {
        // Year
        'yyyy': Y.toString(),
        'yy': Y.toString().slice(-2),

        // Month
        'MMMM': fullMonthNames[monthIndex], // Full month name (e.g., December)
        'MMM': monthNames[monthIndex],      // Abbreviated month name (e.g., Dec)
        'MM': pad(M),
        'M': M.toString(),

        // Day
        'dddd': fullDayNames[dayIndex], // Full day name (e.g., Thursday)
        'ddd': dayNames[dayIndex],     // Abbreviated day name (e.g., Thu)
        'dd': pad(D),
        'd': D.toString(),
        'S': getOrdinalSuffix(D),      // Ordinal suffix (st, nd, rd, th)

        // Hour 24-Hour
        'HH': pad(H),
        'H': H.toString(),

        // Hour 12-Hour
        'hh': pad(h),
        'h': h.toString(),

        // Minute
        'mm': pad(m),
        'm': m.toString(),

        // Second
        'ss': pad(s),
        's': s.toString(),

        // Milliseconds
        'fff': String(f).padStart(3, '0'),

        // AM/PM Designator
        'tt': t,
    };

    // --- Replacement Logic (Must replace longest patterns first) ---

    let output = formatString;

    // Use a regex to find placeholders (y, M, d, H, h, m, s, t, f) and replace them.
    // The expression captures repeated letters (e.g., 'yyyy', 'MM', 'tt')
    const regex = /(yyyy|yy|MMMM|MMM|MM|M|dddd|ddd|dd|d|S|HH|H|hh|h|mm|m|ss|s|fff|ff|f|tt|t)/g;

    output = output.replace(regex, (match) => {
        // Handle slices first (f, ff, t) as they don't have explicit keys
        if (match === 'ff') return values['fff'].substring(0, 2);
        if (match === 'f') return values['fff'].substring(0, 1);
        if (match === 't') return values['tt'].substring(0, 1);
        
        // For all other cases, the match is a key in the 'values' object.
        const key = match as keyof typeof values;
        
        return values[key] || match; // Return the value or the match if not found
    });

    return output;
}

Date.prototype.addDays = function (days: number) {
  var date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
}

String.prototype.toHtmlEntities = function (): string {
  return this.replace(/./gm, function (s) {
    // return "&#" + s.charCodeAt(0) + ";";
    return (s.match(/[a-z0-9\s]+/i)) ? s : "&#" + s.charCodeAt(0) + ";";
  });
}

String.prototype.reverse = function (): string {
  return this.split('').reverse().join('');
}

Math.roundTo = function (value: number | undefined, precision: number): number {
  if (value === undefined) return 0;
  precision = 1 / precision;
  return Math.round((value + Number.EPSILON) * precision) / precision;
}